import { createContext, useContext, useMemo } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { getExperiences, preloadPortfolioData } from '../firebase';
import { useLoading } from './LoadingContext';
import { logError } from '../utils/error-handler';

const ExperiencesContext = createContext(null);

export const ExperiencesProvider = ({ children }) => {
  const [experiences, setExperiences] = useState(getExperiences() || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { finishTask, beginExpectedTasks } = useLoading();

  const loadExperiences = useCallback(async () => {
    setLoading(true);
    setError(null);
    beginExpectedTasks(['experiences']);

    try {
      await preloadPortfolioData();
      const experiencesData = getExperiences();
      setExperiences(experiencesData || []);
    } catch (err) {
      logError(err, 'ExperiencesProvider', {});
      setError(err);
    } finally {
      setLoading(false);
      finishTask('experiences');
    }
  }, [beginExpectedTasks, finishTask]);

  const switchLanguageClientSide = useCallback(() => {
    beginExpectedTasks(['experiences']);

    requestAnimationFrame(() => {
      try {
        const experiencesData = getExperiences();
        setExperiences(experiencesData || []);
      } catch (err) {
        logError(err, 'switchLanguageClientSide - Experiences', {});
        loadExperiences();
      } finally {
        finishTask('experiences');
      }
    });
  }, [beginExpectedTasks, finishTask, loadExperiences]);

  useEffect(() => {
    let isMounted = true;
    loadExperiences();

    const handleLanguageChange = () => {
      if (isMounted) {
        switchLanguageClientSide();
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      isMounted = false;
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [loadExperiences, switchLanguageClientSide]);

  const sortedExperiences = useMemo(() => {
    return Array.isArray(experiences)
      ? [...experiences].sort((a, b) => b.id - a.id)
      : [];
  }, [experiences]);

  const value = useMemo(
    () => ({
      experiences: sortedExperiences,
      loading,
      error,
    }),
    [sortedExperiences, loading, error]
  );

  return (
    <ExperiencesContext.Provider value={value}>
      {children}
    </ExperiencesContext.Provider>
  );
};

export const useExperiences = () => {
  const context = useContext(ExperiencesContext);
  if (!context) {
    throw new Error('useExperiences must be used within ExperiencesProvider');
  }
  return context;
};
